import React, { useEffect, useRef, useState } from "react";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Button from "@mui/material/Button";

import CheckIcon from "@mui/icons-material/Check";
import ErrorIcon from "@mui/icons-material/Error";

import { getTimeFromMilliseconds } from "./util";

const SessionTimeoutDialog = () => {
    const [sessionTimeoutDialogOpen, setSessionTimeoutDialogOpen] = useState(false);
    const [millisecondsUntilSessionTimeout, setMillisecondsUntilSessionTimeout] = useState(600000);

    const [sessionExtensionRequestPending, setSessionExtensionRequestPending] = useState(false);
    const [sessionExtensionRequestSuccessful, setSessionExtensionRequestSuccessful] = useState(false);
    const [sessionExtensionRequestError, setSessionExtensionRequestError] = useState(false);

    const timerRef = useRef(null);

    useEffect(() => {
        handleGetMsUntilSessionExpires();

        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, []);

    const handleGetMsUntilSessionExpires = async () => {
        try {
            const requestOptions = { method: "GET", headers: { "Content-Type": "application/json" } };
            const response = await fetch("api/session/time-remaining", requestOptions);
            if (!response.ok) throw new Error(`An error occured while fetching the time remaining in the session: ${response.statusText}`);

            const msUntilSessionExpires = await response.json();
            handleSetWarningTimer(msUntilSessionExpires);
        } catch (err) {
            console.error(err);
        }
    };
    const handleSetWarningTimer = async (msUntilExpiration) => {
        const tenMinutesMs = 1000 * 60 * 10;

        if (msUntilExpiration < tenMinutesMs) {
            return handleOpenDialog(msUntilExpiration);
        }
        const timer = setTimeout(() => {
            handleOpenDialog(tenMinutesMs);
        }, msUntilExpiration - tenMinutesMs);

        timerRef.current = timer;
    };
    const handleExtendSessionButtonPress = async () => {
        setSessionExtensionRequestPending(true);

        try {
            const requestOptions = { method: "GET", headers: { "Content-Type": "application/json" } };
            const response = await fetch("api/session/extend", requestOptions);
            if (!response.ok) throw new Error(`An error occured while extending your session: ${response.statusText}`);

            const msUntilNextExpiration = await response.json();
            handleSetWarningTimer(msUntilNextExpiration);

            setMillisecondsUntilSessionTimeout(msUntilNextExpiration);
            setSessionExtensionRequestSuccessful(true);
        } catch (err) {
            console.error(err);
            setSessionExtensionRequestError(true);
        } finally {
            setSessionExtensionRequestPending(false);
        }
    };
    const handleCloseDialog = () => {
        setSessionTimeoutDialogOpen(false);
    };
    const handleOpenDialog = (msUntilExpiration) => {
        setMillisecondsUntilSessionTimeout(msUntilExpiration);
        setSessionExtensionRequestError(false);
        setSessionExtensionRequestPending(false);
        setSessionExtensionRequestSuccessful(false);
        setSessionTimeoutDialogOpen(true);
    };
    return (
        <Dialog open={sessionTimeoutDialogOpen} onClose={handleCloseDialog}>
            {sessionExtensionRequestSuccessful === false && sessionExtensionRequestError === false ? (
                <>
                    <DialogTitle>{"Your session is about to expire"}</DialogTitle>

                    <DialogContent>
                        <DialogContentText>{`Your session will expire in ${getTimeFromMilliseconds(millisecondsUntilSessionTimeout)}. Would you like to extend your session?`}</DialogContentText>
                    </DialogContent>

                    <DialogActions>
                        <Button onClick={handleCloseDialog} disabled={sessionExtensionRequestPending}>
                            {"Close"}
                        </Button>
                        <Button onClick={handleExtendSessionButtonPress} loading={sessionExtensionRequestPending}>
                            {"Extend"}
                        </Button>
                    </DialogActions>
                </>
            ) : sessionExtensionRequestSuccessful === true && sessionExtensionRequestError === false ? (
                <>
                    <DialogTitle>{"Session successfully exended!"}</DialogTitle>

                    <DialogContent sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                        <CheckIcon color="success" fontSize="large" />

                        <DialogContentText>
                            {`Your session has successfully been extended. You now have ${getTimeFromMilliseconds(millisecondsUntilSessionTimeout)} before you will need to log in again.`}
                        </DialogContentText>
                    </DialogContent>

                    <DialogActions>
                        <Button onClick={handleCloseDialog}>{"Close"}</Button>
                    </DialogActions>
                </>
            ) : (
                <>
                    <DialogTitle>{"There was an error extending your session"}</DialogTitle>

                    <DialogContent sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                        <ErrorIcon color="error" fontSize="large" />

                        <DialogContentText>
                            {"Your session couldn't be extended at this time. Please log out and log back in to start a new session."}
                        </DialogContentText>
                    </DialogContent>

                    <DialogActions>
                        <Button onClick={handleCloseDialog}>{"Close"}</Button>
                    </DialogActions>
                </>
            )}
        </Dialog>
    );
};

export default SessionTimeoutDialog;
